import { GymTypes, WorkoutType } from 'fitbeat.models';
import { CardioOrResistance, ExerciseFormat } from '../entities/exercise/exercise';
import { WorkoutSettings } from '../entities/user';
import { UserWorkoutPause } from '../entities/workoutsession/userworkoutpause';
import { isStaticCardioZone, isStaticFunctionalZone } from './zone-mappings';

export class WorkoutPausesConfig {
  public static pauseVideoFileNameWithoutExtentionCardio = 'BURN_PAUSE';

  public static pauseVideoFileNameWithoutExtentionResistance = 'BUILD_PAUSE';

  public static pauseVideoFileNameWithoutExtentionCardioHome = 'BURN_PAUSE_HOME';

  public static pauseVideoFileNameWithoutExtentionResistanceHome = 'BUILD_PAUSE_HOME';

  public static pauseVideoFileNameWithoutExtentionBalanceHome = 'BALANCE_PAUSE_HOME';

  public static pauseVideoFileNameWithoutExtentionBalance = 'BALANCE_PAUSE';

  public static pauseVideoFileNameWithoutExtentionBoxing = 'BOXING_PAUSE';

  public static readonly userDefaultWorkoutPausesConfig: Map<number, UserWorkoutPause[]> = new Map([
    [WorkoutType.FullStudioWorkout,
      []],
    [WorkoutType.MediumStudioWorkout,
      []],
    [WorkoutType.QuickStaticFunctionalWorkout,
      []],
    [WorkoutType.QuickHomeOrStaticCardioWorkout, []], // home
    [WorkoutType.MediumHomeOrStaticCardioWorkout, // home
      [new UserWorkoutPause(6, ExerciseFormat.PauseAtHalfwayBreak)]], // home
    [WorkoutType.FullHomeOrStaticCardioWorkout, // home
      [new UserWorkoutPause(6, ExerciseFormat.PauseAtHalfwayBreak),
        new UserWorkoutPause(11, ExerciseFormat.PauseAtHalfwayBreak)]],
    [WorkoutType.ExtraLongHomeOrStaticCardioWorkout, // home
      [new UserWorkoutPause(6, ExerciseFormat.PauseAtHalfwayBreak),
        new UserWorkoutPause(11, ExerciseFormat.PauseAtHalfwayBreak),
        new UserWorkoutPause(16, ExerciseFormat.PauseAtHalfwayBreak)]],
  ]);

  public static readonly userStaticCardioWorkoutPausesConfig: Map<number, UserWorkoutPause[]> = new Map([
    [WorkoutType.QuickHomeOrStaticCardioWorkout,
      []], // 10 min workout with 2 mins setup time
    [WorkoutType.MediumHomeOrStaticCardioWorkout,
      []], // 20 min workout with 2 mins setup time
    [WorkoutType.FullHomeOrStaticCardioWorkout,
      []], // 30 min workout with 2 mins setup time
    [WorkoutType.ExtraLongHomeOrStaticCardioWorkout,
      []], // 40 min workout with 2 mins setup time
  ]);

  public static readonly userStaticFunctionalWorkoutPausesConfig: Map<number, UserWorkoutPause[]> = new Map([
    [WorkoutType.FullStudioWorkout,
      []],
    [WorkoutType.MediumStudioWorkout,
      []], // 28 min workout
    [WorkoutType.QuickStaticFunctionalWorkout,
      []], // 14 min workout
  ]);

  public static userWorkoutPausesConfig = WorkoutPausesConfig.userDefaultWorkoutPausesConfig;

  public static mapPauseFormatToBurnOrBuild(
    pauseType: ExerciseFormat,
    settings: WorkoutSettings,
    gymType: GymTypes,
  ): ExerciseFormat {
    if (pauseType === ExerciseFormat.PauseAtHalfwayBreak && gymType === GymTypes.Home) {
      if (settings.cardioOrResistance === CardioOrResistance.Resistance) {
        return ExerciseFormat.HomeBreakBuild;
      }
      if (settings.cardioOrResistance === CardioOrResistance.Cardio) {
        return ExerciseFormat.HomeBreakBurn;
      }
      return ExerciseFormat.HomeBreakBalance;
    }
    return pauseType;
  }

  public static pausesInWorkout(amountOfStages: number, staticZoneNumber?: number)
    : UserWorkoutPause[] {
    const pauseConfig = this.determinePauseConfig(staticZoneNumber);
    if (typeof amountOfStages === 'string') {
      amountOfStages = parseInt(amountOfStages, undefined);
    }
    const pausesAtAmountOfStages = pauseConfig.get(amountOfStages);
    if (pausesAtAmountOfStages != null) {
      return pausesAtAmountOfStages;
    }
    return [];
  }

  public static isFirstStageAPause(amountOfStages: number, staticZoneNumber?: number): boolean {
    for (const pause of WorkoutPausesConfig.pausesInWorkout(amountOfStages, staticZoneNumber)) {
      if (pause.stage === 1) {
        return true;
      }
    }
    return false;
  }

  private static determinePauseConfig(staticZoneNumber?: number) {
    if (isStaticCardioZone(staticZoneNumber)) {
      return WorkoutPausesConfig.userStaticCardioWorkoutPausesConfig;
    }
    if (isStaticFunctionalZone(staticZoneNumber)) {
      return WorkoutPausesConfig.userStaticFunctionalWorkoutPausesConfig;
    }
    return WorkoutPausesConfig.userWorkoutPausesConfig;
  }
}
